import { timestampDTOSchema } from '@tempo-io/common-dto';
import { z } from 'zod';
import { recommendationFeedbackReasonSchema, recommendationRatingSchema } from '../enums';

/**
 * Same rules as SIR's `recommendationFeedbackBody`: a thumbs-up is a bare `rating`, a thumbs-down
 * carries a preset `reason` or a free-text `comment`, and a body with none of the three is not
 * feedback. `loopId`/`runId` name the run the reader was looking at, so the server can check the
 * finding is really on it and freeze its headline from there rather than from the client.
 */
export const findingFeedbackBodySchema = z
  .object({
    loopId: z.number().int().positive().nullable(),
    runId: z.string().min(1),
    rating: recommendationRatingSchema.optional(),
    reason: recommendationFeedbackReasonSchema.optional(),
    comment: z.string().trim().min(1).max(500).optional(),
  })
  .refine(
    (body) => body.rating !== undefined || body.reason !== undefined || body.comment !== undefined,
    { message: 'Provide a rating, a reason, or a comment' },
  );
export type FindingFeedbackBody = z.output<typeof findingFeedbackBodySchema>;

export const findingFeedbackDTOSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  loopId: z.number().int().positive().nullable(),
  findingId: z.string(),
  /** null = the vote is on the whole finding; set = on one recommendation inside it. */
  recommendationId: z.string().nullable(),
  userId: z.string(),
  /** Soft reference only, no FK: the run may be pruned or become unreadable without taking the vote
   * down with it. */
  runId: z.string(),
  rating: recommendationRatingSchema.nullable(),
  reason: recommendationFeedbackReasonSchema.nullable(),
  comment: z.string().nullable(),
  /** Frozen at write time so the vote stays readable after its run is gone. */
  findingHeadlineSnapshot: z.string(),
  recommendationTitleSnapshot: z.string().nullable(),
  ...timestampDTOSchema.shape,
});
export type FindingFeedbackDTO = z.infer<typeof findingFeedbackDTOSchema>;
